import React from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { defineMessages, useIntl } from 'react-intl';
import API, { Types, useAuth, useCsrfToken } from '@wheeebo/polaris-api';
import { useErrorHandler } from '../errorHandler';
// 指令の送出間隔。サーバー側でCommandLogが1指令=1行なので高頻度にしない。
// CM4ブリッジ側のwatchdog(500ms)より短い周期で送り続ける必要がある。
const SEND_INTERVAL_MS = 200;
const PAD_RADIUS = 70;
const KNOB_RADIUS = 16;
// 連続失敗がこの回数を超えたら心拍を止めて中立にする — 車両側の
// ブリッジ watchdog (500ms) がフェイルセーフを引き継ぐ。
const MAX_CONSECUTIVE_FAILURES = 5;
const msgs = defineMessages({
    title: {
        id: 'components.ManualControlPad.title',
        defaultMessage: 'Manual Control',
    },
    hint: {
        id: 'components.ManualControlPad.hint',
        defaultMessage: 'Hold and drag to drive. Release to stop.',
    },
    linkLost: {
        id: 'components.ManualControlPad.linkLost',
        defaultMessage: 'Control link lost — commands are not reaching the server.',
    },
    unsupported: {
        id: 'components.ManualControlPad.unsupported',
        defaultMessage: 'This browser does not support pointer events.',
    },
});
function clampAxis(v) {
    return Math.max(-1, Math.min(1, v));
}
function ManualControlPad({ vehicle }) {
    const intl = useIntl();
    const theme = useTheme();
    const { account } = useAuth();
    const { csrfToken } = useCsrfToken();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const padRef = React.useRef(null);
    const [knob, setKnob] = React.useState({ x: 0, y: 0 });
    const [dragging, setDragging] = React.useState(false);
    const [linkLost, setLinkLost] = React.useState(false);
    const axesRef = React.useRef({ x: 0, y: 0 });
    const activePointerRef = React.useRef(null);
    const everDroveRef = React.useRef(false);
    const failCountRef = React.useRef(0);
    const pointerSupported = typeof window !== 'undefined' && 'PointerEvent' in window;
    const send = React.useCallback((x, y) => {
        if (!account?.id || !csrfToken) {
            // account.id is empty during session restore and csrfToken loads
            // async — sending then produces a silent nosurf 400 flood.
            return;
        }
        API.Vehicles.command(csrfToken, vehicle.id, account.id, Types.VehicleCommandManualControl, { x, y })
            .then(() => {
            failCountRef.current = 0;
            setLinkLost(false);
        })
            .catch((e) => {
            failCountRef.current += 1;
            setLinkLost(true);
            if (failCountRef.current === 1) {
                showAndEmitJsErrorEvent(e);
            }
        });
    }, [account, csrfToken, vehicle.id, showAndEmitJsErrorEvent]);
    // Keep a current send in a ref so cleanup/lifecycle handlers never capture
    // a stale closure (mount-time send has no account/token yet).
    const sendRef = React.useRef(send);
    sendRef.current = send;
    const sendNeutral = React.useCallback(() => {
        if (everDroveRef.current) {
            sendRef.current(0, 0);
            everDroveRef.current = false;
        }
    }, []);
    const neutral = React.useCallback(() => {
        axesRef.current = { x: 0, y: 0 };
        setKnob({ x: 0, y: 0 });
        send(0, 0);
        // A second neutral ~250ms later covers the release-ordering race where an
        // older non-zero frame lands in the depth-1 mailbox after this one.
        setTimeout(() => sendRef.current(0, 0), 250);
    }, [send]);
    const updateFromPointer = React.useCallback((clientX, clientY) => {
        const rect = padRef.current?.getBoundingClientRect();
        if (!rect) {
            return;
        }
        let dx = clientX - (rect.left + rect.width / 2);
        let dy = clientY - (rect.top + rect.height / 2);
        const dist = Math.hypot(dx, dy);
        if (dist > PAD_RADIUS) {
            dx = (dx / dist) * PAD_RADIUS;
            dy = (dy / dist) * PAD_RADIUS;
        }
        setKnob({ x: dx, y: dy });
        // 画面上が+forward。x=右舷が正。浮動小数点の丸めで ±1.0000000002 に
        // なることがありサーバ側 lte=1 検証で 400 になるため clamp する。
        axesRef.current = { x: clampAxis(dx / PAD_RADIUS), y: clampAxis(-dy / PAD_RADIUS) };
        everDroveRef.current = true;
    }, []);
    const endDrag = React.useCallback(() => {
        activePointerRef.current = null;
        setDragging(false);
        neutral();
    }, [neutral]);
    // 倒している間だけ定期送信(デッドマン心拍)。axes.x/y を deps に入れると
    // pointermove (~60-120Hz) のたびにエフェクトが再発火して HTTP フラッドに
    // なるため、間隔内の最新値は axesRef 経由で読む。
    // 連続失敗が閾値を超えたら心拍停止+中立 — 以後は車両側ブリッジの
    // 500ms watchdog がニュートラルを保証する。
    React.useEffect(() => {
        if (!dragging) {
            return;
        }
        sendRef.current(axesRef.current.x, axesRef.current.y);
        const id = setInterval(() => {
            if (failCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
                setDragging(false);
                sendNeutral();
                return;
            }
            sendRef.current(axesRef.current.x, axesRef.current.y);
        }, SEND_INTERVAL_MS);
        return () => clearInterval(id);
    }, [dragging, sendNeutral]);
    // アンマウント/ページ離脱/タブ非表示時は中立を送る。iOS Safari は
    // beforeunload を発火しないため pagehide / visibilitychange も購読。
    React.useEffect(() => {
        const onHide = () => sendNeutral();
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                // hidden タブは setInterval が ~1Hz に間引かれ 500ms ブリッジ
                // watchdog を下回る — スロットルされる前に明示的に止める。
                setDragging(false);
                sendNeutral();
            }
        };
        window.addEventListener('beforeunload', onHide);
        window.addEventListener('pagehide', onHide);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('beforeunload', onHide);
            window.removeEventListener('pagehide', onHide);
            document.removeEventListener('visibilitychange', onVisibility);
            sendNeutral();
        };
    }, [sendNeutral]);
    return (React.createElement(Stack, { gap: 0.5, sx: { alignItems: 'center' } },
        React.createElement(Typography, { variant: "caption" }, intl.formatMessage(msgs.title)),
        React.createElement(Box, { ref: padRef, onPointerDown: (e) => {
                if (!pointerSupported || activePointerRef.current !== null) {
                    return;
                }
                e.currentTarget.setPointerCapture(e.pointerId);
                activePointerRef.current = e.pointerId;
                setDragging(true);
                updateFromPointer(e.clientX, e.clientY);
            }, onPointerMove: (e) => {
                if (dragging && e.pointerId === activePointerRef.current) {
                    updateFromPointer(e.clientX, e.clientY);
                }
            }, onPointerUp: (e) => {
                if (e.pointerId === activePointerRef.current) {
                    endDrag();
                }
            }, onPointerCancel: (e) => {
                if (e.pointerId === activePointerRef.current) {
                    endDrag();
                }
            }, onLostPointerCapture: (e) => {
                // OS ジェスチャー等でキャプチャが奪われた場合も心拍を止める —
                // 放置すると指を離した後も最後の指令が打ち続ける。
                if (e.pointerId === activePointerRef.current) {
                    endDrag();
                }
            }, sx: {
                width: PAD_RADIUS * 2,
                height: PAD_RADIUS * 2,
                borderRadius: '50%',
                border: `2px solid ${dragging ? theme.palette.primary.main : theme.palette.divider}`,
                position: 'relative',
                touchAction: 'none',
                userSelect: 'none',
                cursor: dragging ? 'grabbing' : 'grab',
                bgcolor: theme.palette.action.hover,
            } },
            React.createElement(Box, { sx: {
                    width: KNOB_RADIUS * 2,
                    height: KNOB_RADIUS * 2,
                    borderRadius: '50%',
                    bgcolor: dragging ? theme.palette.primary.main : theme.palette.text.secondary,
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))`,
                    pointerEvents: 'none',
                } })),
        React.createElement(Typography, { variant: "caption", color: "text.secondary" }, intl.formatMessage(msgs.hint)),
        linkLost ? (React.createElement(Alert, { severity: "error", variant: "outlined", sx: { py: 0 } }, intl.formatMessage(msgs.linkLost))) : null,
        !pointerSupported ? (React.createElement(Alert, { severity: "error", variant: "outlined", sx: { py: 0 } }, intl.formatMessage(msgs.unsupported))) : null,
        vehicle.vehicleType !== Types.VehicleTypeAsv ? (React.createElement(Alert, { severity: "warning", variant: "outlined", sx: { py: 0 } }, "This control is intended for ASV (surface) vehicles.")) : null));
}
export default ManualControlPad;
